import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@injectables/services/errors/error-handler.service';
import { InvalidAddressCacheService } from './invalid-address-cache.service';
import { Project } from 'domain-entities';

const DEFAULT_LOCALE = 'de-DE';
const GERMAN_LOCALE_REGEX = '^de-?';
const GOOGLE_MAPS_LOAD_TIMEOUT = 10000;

enum LocalPrefix {
	DE = 'de',
	EN = 'en',
}

interface Location {
	lat: string;
	lon: string;
}

@Injectable({
	providedIn: 'root',
})
export class GeoLocationService {
	private geocoder: google.maps.Geocoder | null = null;

	constructor(
		private readonly errorHandler: ErrorHandlerService,
		private readonly invalidAddressCache: InvalidAddressCacheService
	) {}

	private async ensureGeocoder(): Promise<google.maps.Geocoder> {
		if (this.geocoder) return this.geocoder;

		if (typeof google === 'undefined' || !google.maps) {
			await this.waitForGoogleMaps();
		}

		this.geocoder = new google.maps.Geocoder();
		return this.geocoder;
	}

	private waitForGoogleMaps(): Promise<void> {
		return new Promise((resolve, reject) => {
			const timeout = setTimeout(() => {
				reject(new Error('Google Maps failed to load'));
			}, GOOGLE_MAPS_LOAD_TIMEOUT);

			const checkGoogleMaps = (): void => {
				if (typeof google !== 'undefined' && google.maps) {
					clearTimeout(timeout);
					resolve();
					return;
				}
				setTimeout(checkGoogleMaps, 100);
			};

			checkGoogleMaps();
		});
	}

	async getLocationFromAddress(project: Project): Promise<Location | null> {
		try {
			if (this.invalidAddressCache.isAddressInvalid(project)) return null;

			const location = await this.getLocationFromGoogleMaps(project);
			if (!location) {
				this.invalidAddressCache.markAddressAsInvalid(project);
			}
			return location;
		} catch (error) {
			this.errorHandler.handleError(error);
			return null;
		}
	}

	private async getLocationFromGoogleMaps(project: Project): Promise<Location | null> {
		if (!this.hasMinimumAddressComponents(project)) {
			return null;
		}

		try {
			const address = this.getAddressString(project);
			if (!address) return null;

			const geocoder = await this.ensureGeocoder();
			const response = await geocoder.geocode({ address });

			if (response.results && response.results.length > 0) {
				const location = response.results[0].geometry.location;
				return {
					lat: location.lat().toString(),
					lon: location.lng().toString(),
				};
			}

			// If we get here, it means no results were found
			this.invalidAddressCache.markAddressAsInvalid(project);
			console.error("No geocoding results found for address:", address);
			return null;
		} catch (error) {
			if (error instanceof Error) {
				// Handle specific error cases that might come from the geocoder
				if (error.message.includes("INVALID_REQUEST")) {
					console.error("Invalid geocoding request");
					this.invalidAddressCache.markAddressAsInvalid(project);
				} else if (error.message.includes("OVER_QUERY_LIMIT") || error.message.includes("REQUEST_DENIED")) {
					console.error("Geocoding API error:", error.message);
				} else if (error.message.includes("UNKNOWN_ERROR")) {
					console.error("Temporary server error, may retry");
				} else {
					console.error("Geocoding error:", error.message);
				}
			} else {
				console.error("Unknown geocoding error:", error);
			}
			return null;
		}
	}

	private hasMinimumAddressComponents(project: Project): boolean {
		if (!project?.street) return false;
		return !!(project.city || project.zipcode);
	}

	private getAddressString(project: Project): string {
		if (!project) return '';

		const parts = [];
		if (project.street) parts.push(project.street);
		if (project.city) parts.push(project.city);
		if (project.zipcode) parts.push(project.zipcode);
		if (project.country) parts.push(project.country);

		return parts.join(', ');
	}

	get locale(): string {
		const locale = navigator.language;
		if (!locale) {
			return DEFAULT_LOCALE;
		}
		if (isValidLocale(locale)) {
			return locale;
		}
		return 'en-US';
	}

	getLocalePrefix(): LocalPrefix {
		if (this.locale.startsWith('de')) {
			return LocalPrefix.DE;
		} else {
			return LocalPrefix.EN;
		}
	}

	getTimezone(): string {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	}
}

const isValidLocale: (locale: string) => boolean = (locale: string) => {
	return !!locale.match(GERMAN_LOCALE_REGEX);
};
